import React, {
  useContext,
  useMemo,
  useEffect,
  memo,
  useCallback,
  useState,
} from 'react';
import { findCollectionStatic } from '../../../../../../Model/avenyStaticModelUtils';
import { MdModalRoute } from '../../../../../../Components/MasterDetail/Components/MdModal/MdModalRoute';
import {
  IDetailCompProps,
  IMasterCompProps,
} from '../../../../../../Types/MasterDetailTypes';
import { StandardVy } from '../StandardVy';
import EDIconButton from '../../../../../../BaseComponents/Buttons/IconButton/EDIconButton';
import { createPortal } from 'react-dom';
import BusinessDataObjectDetailHeader from '../BusinessDataObjectDetailHeader/BusinessDataObjectDetailHeader';
import { nth } from 'ramda';
import { BreadcrumbsContext } from '../../RutinBreadcrumbs/BreadcrumbsContext';
import { ISuggestion } from '../../../../../../BaseComponents/AutoCompleteEdit/EDAutoCompleteEdit';
import { getAttributeHeaderValue } from '../../../../../../Model/avenyModelUtils';
import ObjektLankHeader from './ObjektLankHeader';
import { InternalError } from '../../../../../../Components/InternalError/InternalError';
import { IClientBusinessModel } from '../../../../../../Types/ClientModelTypes';
import { INodeData } from '../../../../../../Components/QueryComponents/NodeQuery/NodeQuery';
import { AttributeValueSizeType } from '../../../../../../Components/Attributes/AttributeValue/AttributeValue';
import EDCaptionedValue from '../../../../../../BaseComponents/CaptionedValue/EDCaptionedValue';
import {
  findHeaderColumns,
  IBusinessConfig,
} from '../../../Const/BusinessConfigs';
import { GridContext } from '../../../../../GridContext';
import { CommonPropsAndTypes } from '../../../../../../BaseComponents/_Types/StandardComponentProps';
import { ICustomValidValues } from '../../../../../../Types/ColumnDefType';
import { useBusinessConfig } from '../../../../../../Components/BusinessConfigContext/BusinessConfigContext';
import {
  IUseAssociationValuesResult,
  useAssociationValues,
} from '../../../Hooks/useAssociationValues';
import { definedOrThrow } from '../../../../../../Utils/NullableUtils';
import { AssociationAutocomplete } from '../AssociationChoice/AssociationAutocomplete';
import { BusinessDataFormContext } from '../BusinessDataForm/BusinessDataForm';

interface IObjektLankProps<T> extends CommonPropsAndTypes.IMarginProps {
  collection?: IClientBusinessModel;
  data?: INodeData;
  editedValues?: INodeData;
  /** onAssociationSelected körs istället för den vanliga uppdateringen av formstate*/
  onAssociationSelected?: (suggestion: ISuggestion | null) => void;
  /** afterAssociationSelected körs efter den vanliga uppdateringen av formstate*/
  afterAssociationSelected?: (suggestion: ISuggestion | null) => void;
  value?: string;
  associationName: string;
  label?: string;
  id?: string | null;
  size?: AttributeValueSizeType;
  required?: boolean;
  editMode?: boolean;
  tooltip?: string | null;
  headerDefs?: string[] | string;
  /** Det går inte att klicka sig vidare  */
  disableAssociationLink?: boolean;
  disabled?: boolean;
  errorMessage?: string;
  warningMessage?: string;
  hintMessage?: string;
  fullWidth?: boolean;
  hideLabel?: boolean;
  hideOutline?: boolean;
  // topMargin?: CommonPropsAndTypes.StandardSpacingType;
  validValues?: ICustomValidValues;
  /** Manuell inställning av ändringsmarkören, visas annars när fältet är ändrat */
  marker?: boolean;
  /** Visa inte ändringsmarkeringen när fältet är ändrat (överstyrs av marker om den är satt) */
  disableChangeMarker?: boolean;
  lineMarker?: boolean;
  disableDisabledBackgroundColor?: boolean;
  rowID?: string;
  inputRef?: React.MutableRefObject<HTMLInputElement | undefined>;
  onFocus?: (event: React.FocusEvent<HTMLElement, Element>) => void;
  disableTabbing?: boolean;
  setOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  testId?: string;
}

interface IBaseObjektLankProps<T> extends IObjektLankProps<T> {
  associationValues: IUseAssociationValuesResult;
  businessConfig?: IBusinessConfig;
  inGrid: boolean;
}

function BaseObjektLank<T>(
  props: IBaseObjektLankProps<T>
): React.ReactElement<any> {
  const formStateDispatch = props.associationValues.formStateDispatch;
  const update = props.associationValues.update;
  const id =
    props.id ??
    props.associationValues.data?._Associations?.[props.associationName]?.id;
  const model = definedOrThrow('ObjektLank saknar modell')(
    props.collection || props.associationValues.model
  );
  const businessConfig = props.businessConfig;
  // const businessConfig = undefined;
  const assoc = model.Associations[props.associationName];
  const associationConfig =
    businessConfig?.associations?.[props.associationName];
  const editMode = props.editMode ?? props.associationValues.editMode;
  const afterAssociationSelected = props.afterAssociationSelected;

  const headerDefs = useMemo(() => {
    return props.headerDefs
      ? Array.isArray(props.headerDefs)
        ? props.headerDefs
        : [props.headerDefs]
      : findHeaderColumns(assoc.AssociatedCollectionName);
  }, [props.headerDefs, assoc.AssociatedCollectionName]);

  const data = props.data ?? props.associationValues.data;

  const onAssociationSelected = useCallback(
    (suggestion: ISuggestion | null) => {
      update?.([
        {
          operation: 'Ref',
          associationName: props.associationName,
          values: suggestion
            ? {
                id: suggestion.value,
                ...suggestion?.data,
              }
            : null,
        },
      ]);
      afterAssociationSelected?.(suggestion);
    },
    [update, props.associationName, afterAssociationSelected]
  );

  const value = props.value ?? props.associationValues.value;

  const touched = props.associationValues.touched;

  const label = data
    ? getAttributeHeaderValue(data, props.associationName as string, headerDefs)
    : '';

  //Om det finns en hint om att fältet är required
  const validationRequiredValueHint = props.associationValues.validations.find(
    val => val.type === 'RequiredValueHint'
  );
  const validationIsRequired = Boolean(validationRequiredValueHint);
  const validationIsRequiredMessage = validationRequiredValueHint?.message;

  const required = props.required ?? assoc.Required ?? validationIsRequired;

  const errorMessage = props.associationValues.validations
    .filter(
      val =>
        (val.type === 'Error' && !val.showAsHint) ||
        (val.type === 'RequiredValueHint' &&
          !val.showAsHint &&
          !value &&
          val.message != undefined)
    )
    .map(val => val.message)
    .concat([
      ...(required && !value && touched
        ? [validationIsRequiredMessage ?? 'Värde krävs']
        : []),
    ])
    .join(', ');
  const warningMessage = props.associationValues.validations
    .filter(val => val.type === 'Warning' && !val.showAsHint)
    .map(val => val.message)
    .join(', ');
  const hintMessage = props.associationValues.validations
    .filter(val => val.type === 'Hint' || val.showAsHint)
    .map(val => val.message)
    .join(', ');

  const associatedCollection = useMemo(
    () => findCollectionStatic(assoc.AssociatedCollectionName),
    [assoc.AssociatedCollectionName]
  );

  const master = useMemo(() => {
    return {
      caption: model.RootType.SBLabel,
      comp: (masterprops: IMasterCompProps) => {
        const {
          headerReferences,
          currentNavLevel,
          topNavLevel,
          backButtonReferences,
          registerActiveNavLevel,
          unregisterActiveNavLevel,
          // eslint-disable-next-line react-hooks/rules-of-hooks
        } = useContext(BreadcrumbsContext);
        const headerRef =
          masterprops.selectedDetail && currentNavLevel !== undefined
            ? headerReferences[currentNavLevel]
            : undefined;
        const renderHeader = headerRef != null;
        // eslint-disable-next-line react-hooks/rules-of-hooks
        useEffect(() => {
          if (renderHeader && currentNavLevel) {
            registerActiveNavLevel(currentNavLevel);
            return () => unregisterActiveNavLevel(currentNavLevel);
          }
          return undefined;
        }, [
          renderHeader,
          currentNavLevel,
          registerActiveNavLevel,
          unregisterActiveNavLevel,
        ]);

        const backRef =
          masterprops.selectedDetail &&
          currentNavLevel !== undefined &&
          currentNavLevel === topNavLevel
            ? nth(currentNavLevel, backButtonReferences) || null
            : null;
        const unselectDetail = () => masterprops.selectDetail(null);
        const selectCurrentDetail = () =>
          masterprops.selectDetail(masterprops.selectedDetail);

        return (
          <>
            <EDCaptionedValue
              label={props.label || assoc.DisplayLabel}
              hideLabel={props.hideLabel}
              name={assoc.AssociationName}
              size={props.size}
            >
              <ObjektLankHeader
                label={label}
                disableClickable={props.disableAssociationLink}
                onClick={() => masterprops.selectDetail(id)}
                testId={`${props.testId}-label`}
              />
            </EDCaptionedValue>
            <BusinessDataObjectDetailHeader
              collectionName={assoc.AssociatedCollectionName}
              id={id}
              headerRef={headerRef}
              caption={assoc.DisplayLabel}
              onClick={
                currentNavLevel !== topNavLevel
                  ? selectCurrentDetail
                  : undefined
              }
            />
            {backRef &&
              createPortal(
                <EDIconButton
                  icon="arrow_back"
                  color={'inherit'}
                  tooltip={`Tillbaka till ${model.RootType.SBLabel}`}
                  padding="dense"
                  marginTop={0.5}
                  marginBottom={0.5}
                  onClick={unselectDetail}
                />,
                backRef
              )}
          </>
        );
      },
    };
  }, [
    model.RootType.SBLabel,
    props.label,
    props.hideLabel,
    props.size,
    props.disableAssociationLink,
    props.testId,
    assoc.DisplayLabel,
    assoc.AssociationName,
    assoc.AssociatedCollectionName,
    label,
    id,
  ]);

  const detail = useMemo(() => {
    return {
      caption: assoc.DisplayLabel,
      comp: (detailprops: IDetailCompProps) =>
        associatedCollection ? (
          <StandardVy bmod={associatedCollection} id={id} />
        ) : null,
    };
  }, [assoc, associatedCollection, id]);

  const onBlur = useCallback(() => {
    if (formStateDispatch) {
      formStateDispatch({
        type: 'TOUCH',
        path: ['_Associations', props.associationName],
      });
    }
  }, [formStateDispatch, props.associationName]);

  if (associatedCollection) {
    if (!assoc) {
      return (
        <InternalError
          id="5ab7ab84-c7c1-42ca-a335-27f306a69d3f"
          message={`Associationen: ${props.associationName} saknas i ${model.CollectionName}`}
        />
      );
    }

    return (
      <>
        {!editMode && (
          <MdModalRoute
            detailRouteName={assoc.AssociationName}
            master={master}
            detail={detail}
            collection={associatedCollection}
          />
        )}
        {editMode && (
          <AssociationAutocomplete
            options={props.associationValues.options}
            value={value}
            onAssociationSelected={
              props.onAssociationSelected ?? onAssociationSelected
            }
            label={props.label ?? assoc.DisplayLabel}
            editMode
            relativeSize
            required={required}
            requiredMessage={validationIsRequiredMessage}
            marker={
              props.marker ??
              (!props.disableChangeMarker && props.associationValues.isEdited)
            }
            inGrid={props.inGrid}
            size={props.size}
            tooltip={props.tooltip}
            disabled={props.disabled}
            errorMessage={props.errorMessage ?? errorMessage}
            warningMessage={props.warningMessage ?? warningMessage}
            hintMessage={props.hintMessage ?? hintMessage}
            fullWidth={props.fullWidth}
            hideLabel={props.hideLabel}
            hideOutline={props.hideOutline}
            allMargins={props.allMargins}
            topMargin={props.topMargin}
            inputMargin={props.allMargins}
            disableArrowIcon={associationConfig?.disableArrowIcon}
            lineMarker={props.lineMarker}
            disableDisabledBackgroundColor={
              props.disableDisabledBackgroundColor
            }
            inputRef={props.inputRef}
            testId={props.testId ?? props.associationName}
            onBlur={onBlur}
            onFocus={props.onFocus}
            disableTabbing={props.disableTabbing}
            setOpen={props.setOpen}
          />
        )}
      </>
    );
  }
  return <div />;
}

const MemoBaseObjektLank: React.FC<
  React.PropsWithChildren<IBaseObjektLankProps<any>>
> = memo(BaseObjektLank);

export function ObjektLank<T>(
  props: IObjektLankProps<T>
): React.ReactElement<any> | null {
  const [open, setOpen] = useState(false);
  const headerDefs: string[] | undefined = useMemo(
    () =>
      props.headerDefs === undefined
        ? undefined
        : Array.isArray(props.headerDefs)
        ? props.headerDefs
        : [props.headerDefs],
    [props.headerDefs]
  );
  const businessDataForm = useContext(BusinessDataFormContext);
  const associationValues = useAssociationValues(
    props.associationName as string,
    props.collection,
    headerDefs,
    props.validValues,
    open,
    businessDataForm?.formState.loading
  );
  const businessConfig = useBusinessConfig(
    associationValues.model?.CollectionName || ''
  );
  const gridContext = useContext(GridContext);
  return (
    <MemoBaseObjektLank
      associationValues={associationValues}
      businessConfig={businessConfig}
      inGrid={gridContext.inGrid}
      setOpen={setOpen}
      {...props}
    />
  );
}
